import type { SearchPosting } from "@/lib/api/catalogue";
import type { EnvoySettings } from "@/lib/api/envoy";
import { WORK_SETTING_LABEL } from "./labels";

/**
 * Places: countries and states by name, the envoy's where, and a
 * posting's own. Pure.
 */

/** A country's name in English from its ISO code, or the code when it has none. */
export function countryName(code: string): string {
    try {
        return new Intl.DisplayNames(["en"], { type: "region" }).of(code.toUpperCase()) ?? code;
    } catch {
        return code;
    }
}

/** A state's code without its country: `US-UT` is `UT`. */
export function regionCode(code: string): string {
    const dash = code.indexOf("-");
    return dash < 0 ? code : code.slice(dash + 1);
}

/** One country of the envoy's where. */
export interface WhereChip {
    code: string;
    name: string;
}

export function whereChips(settings: EnvoySettings): WhereChip[] {
    return settings.countries.map((code) => ({ code, name: countryName(code) }));
}

/**
 * Where a posting is: the board's own words, or, when it names several
 * places and the search knows where the person is, the one nearest them.
 */
function placeOf(posting: SearchPosting): string | null {
    const several = Math.max(posting.locations.length, posting.cities.length) > 1;
    const nearest = posting.nearest_place?.trim() || null;
    if (several && nearest) return nearest;
    return posting.location?.trim() || null;
}

/** How a posting is worked, in words, from the board or else the reading. */
function settingOf(posting: SearchPosting): string | null {
    const setting = posting.work_setting ?? posting.reading?.work_setting ?? null;
    return setting ? WORK_SETTING_LABEL[setting] : null;
}

/** "Remote, US", "Hybrid, Denver, CO": how it is worked, then where, said once. */
export function whereLine(posting: SearchPosting): string {
    const label = settingOf(posting);
    const location = placeOf(posting);
    if (!label) return location ?? "";
    if (!location) return label;
    if (location.toLowerCase().startsWith(label.toLowerCase())) return location;
    return `${label}, ${location}`;
}

/**
 * "Seattle, WA · Hybrid": where, then how it is worked, said once. One
 * office goes by the catalogue's short name, not the board's full address.
 */
export function placeAndSetting(posting: SearchPosting): string {
    const label = settingOf(posting);
    const onlyCity = posting.cities.length === 1 ? posting.cities[0]?.trim() : null;
    const location = onlyCity || placeOf(posting);
    if (!label) return location ?? "";
    if (!location) return label;
    if (location.toLowerCase().startsWith(label.toLowerCase())) return location;
    return `${location} · ${label}`;
}
